import { useState } from 'react'
import { Card, CardContent, CardHeader, Button } from '@repo/ui-core'
import { Phone, Mail, Building2, UserCircle, CheckCircle2, Circle, AlertCircle } from 'lucide-react'

// Mock data based on schema for the UI
const MOCK_CONTACTS = [
  { id: '1', type: 'individual', name: 'Juan Dela Cruz', email: 'juan@example.com', phone: '0917-123-4567', arCustomerId: 'CUST-001' },
  { id: '2', type: 'company', name: 'Tech Solutions Inc', tinNumber: '123-456-789-000', arCustomerId: 'CUST-002' }
]

const MOCK_DEALS = [
  { id: '1', title: 'ERP Implementation', stage: 'prospect', valueAmount: 150000, probabilityPercent: 10 },
  { id: '2', title: 'Annual Tax Filing', stage: 'proposal', valueAmount: 45000, probabilityPercent: 50 },
  { id: '3', title: 'Audit Retainer', stage: 'negotiation', valueAmount: 120000, probabilityPercent: 80 },
  { id: '4', title: 'Consulting Services', stage: 'won', valueAmount: 85000, probabilityPercent: 100 }
]

const MOCK_ACTIVITIES = [
  { id: '1', type: 'call', title: 'Intro call with Juan', isCompleted: true, dueDate: Date.now() - 86400000 },
  { id: '2', type: 'meeting', title: 'Proposal presentation', isCompleted: false, dueDate: Date.now() + 86400000 },
  { id: '3', type: 'email', title: 'Send revised contract', isCompleted: false, dueDate: Date.now() + 172800000 }
]

export const CrmDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<'pipeline' | 'contacts' | 'activities'>('pipeline')
  const [selectedDeal, setSelectedDeal] = useState<(typeof MOCK_DEALS)[0] | null>(null)

  const formatPHP = (amount: number) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount)

  const STAGES = ['prospect', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] as const

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">CRM Dashboard</h1>
          <p className="text-sm text-gray-500">Manage contacts, deal pipeline, and follow-ups</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">Import Contacts</Button>
          <Button>New Deal</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('pipeline')}
          className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'pipeline' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Deal Pipeline
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'contacts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Contacts &amp; Leads
        </button>
        <button
          onClick={() => setActiveTab('activities')}
          className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'activities' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Activities Feed
        </button>
      </div>

      {/* Pipeline Kanban */}
      {activeTab === 'pipeline' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(stage => {
            const stageDeals = MOCK_DEALS.filter(d => d.stage === stage)
            const stageTotal = stageDeals.reduce((sum, d) => sum + d.valueAmount, 0)

            return (
              <div key={stage} className="flex-none w-80 bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-700 capitalize">{stage}</h3>
                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">{stageDeals.length}</span>
                </div>
                <div className="text-sm text-gray-500 mb-3">{formatPHP(stageTotal)}</div>

                <div className="space-y-3">
                  {stageDeals.map(deal => (
                    <div
                      key={deal.id}
                      className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedDeal(deal)}
                    >
                      <div className="font-medium text-gray-900 mb-1">{deal.title}</div>
                      <div className="text-sm text-blue-600 font-semibold mb-2">{formatPHP(deal.valueAmount)}</div>
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {deal.probabilityPercent}% win
                        </span>
                      </div>
                    </div>
                  ))}
                  {stageDeals.length === 0 && (
                    <div className="text-center py-6 text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                      No deals
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Contacts List */}
      {activeTab === 'contacts' && (
        <Card>
          <CardHeader title="Directory" />
          <CardContent>
            <div className="divide-y divide-gray-200">
              {MOCK_CONTACTS.map(contact => (
                <div key={contact.id} className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                      {contact.type === 'company' ? <Building2 className="w-5 h-5" /> : <UserCircle className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {contact.name}
                        {contact.arCustomerId && (
                          <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full">
                            Linked: AR
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-4 mt-1">
                        {contact.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {contact.email}</span>}
                        {contact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {contact.phone}</span>}
                        {'tinNumber' in contact && contact.tinNumber && <span className="flex items-center gap-1">TIN: {contact.tinNumber}</span>}
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">View</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activities Feed */}
      {activeTab === 'activities' && (
        <Card>
          <CardHeader title="Upcoming Follow-ups &amp; History" />
          <CardContent>
            <div className="space-y-4">
              {MOCK_ACTIVITIES.map(activity => (
                <div key={activity.id} className="flex gap-4 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                  <button className="mt-0.5 text-gray-400 hover:text-green-500">
                    {activity.isCompleted ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <Circle className="w-5 h-5" />}
                  </button>
                  <div className="flex-1">
                    <div className={`font-medium ${activity.isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {activity.title}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 flex gap-3">
                      <span className="capitalize px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {activity.type}
                      </span>
                      <span>
                        {new Date(activity.dueDate).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between border-b p-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedDeal.title}</h2>
                <div className="text-sm text-gray-500 mt-1">
                  Value: {formatPHP(selectedDeal.valueAmount)} • {selectedDeal.probabilityPercent}% Probability
                </div>
              </div>
              <Button variant="outline" onClick={() => setSelectedDeal(null)}>Close</Button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex gap-4 items-center">
                <span className="font-medium text-gray-700">Stage:</span>
                <select
                  className="border rounded p-2 text-sm bg-gray-50 flex-1"
                  defaultValue={selectedDeal.stage}
                >
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {selectedDeal.stage === 'won' && (
                  <Button className="bg-green-600 hover:bg-green-700">Convert to Sales Order</Button>
                )}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-900">Activity Timeline</h3>
                  <Button variant="outline" size="sm">Log Activity</Button>
                </div>
                <div className="space-y-3">
                  {MOCK_ACTIVITIES.map(act => (
                    <div key={act.id} className="text-sm p-3 border rounded-lg bg-gray-50">
                      <div className="flex justify-between">
                        <strong className="text-gray-800">{act.title}</strong>
                        <span className="text-gray-500">{new Date(act.dueDate).toLocaleDateString()}</span>
                      </div>
                      <div className="text-gray-600 mt-1">Type: {act.type} • Status: {act.isCompleted ? 'Done' : 'Pending'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
